import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EAdvancedFilterLogic } from '../../../filters-advanced/models/advanced-filter-logic.enum';
import {
  TSearchFilter,
  TSearchFilterRow,
} from '../../../filters-advanced/models/advanced-filter.model';
import {
  EPointSearchOperator,
  TSearchFilterSubgroup,
  TSearchResponseDTO,
} from '../../../points/point-search/point-search.consts';
import {
  EReminderFilterKeys,
  EReminderFilterType,
} from '../../../reminders/reminder-filters/reminder-filters.consts';
import { UserService } from '../../../user/user.service';
import { generateBetweenFilter } from '../search/generate-between-filter';
import { EReminderDTOKeys } from '../search/quick-filter.consts';
import { GET_DEFAULT_REMINDER_FILTERS } from './quick-reminder-filter.service.utils';

@Injectable({
  providedIn: 'root',
})
export class QuickReminderFilterService {
  private filters: TSearchFilter[] = GET_DEFAULT_REMINDER_FILTERS();
  private keyword = '';

  private readonly _filtersChange$ = new Subject<TSearchFilter[]>();
  private readonly filtersChange$ = this._filtersChange$.asObservable();

  constructor(private userService: UserService) {}

  setFilters(filters: TSearchFilter[]): void {
    this.filters = filters;
    this.emitFiltersChange();
  }

  getFilters(): TSearchFilter[] {
    return this.filters;
  }

  getFiltersChange$(): Observable<TSearchFilter[]> {
    return this.filtersChange$;
  }

  resetFilters(): void {
    this.filters = GET_DEFAULT_REMINDER_FILTERS();
    this.emitFiltersChange();
  }

  setKeyword(keyword: string): void {
    this.keyword = keyword;
    this.emitFiltersChange();
  }

  getFiltersTransformed(seriesId: string): TSearchResponseDTO['filter'] {
    const quickFilters = [...this.filters];
    const defaultFilters: TSearchFilter[] = [
      {
        keys: [EReminderFilterType.TYPE],
        values: [],
        operator: EPointSearchOperator.IN,
      },
      {
        keys: [EReminderFilterType.STATUS],
        values: [],
        operator: EPointSearchOperator.IN,
      },
      {
        keys: [EReminderFilterType.LINKED_TO],
        values: [],
        operator: EPointSearchOperator.IN,
      },
    ];

    defaultFilters.forEach((defaultFilter) => {
      const existingFilter = quickFilters.find(
        (filter) => filter.keys[0] === defaultFilter.keys[0],
      );

      if (!existingFilter) {
        quickFilters.push(defaultFilter);
      }
    });

    const quickFiltersTransformed: TSearchFilterSubgroup[] = quickFilters
      .filter((filter) => filter.operator && filter.keys?.length)
      .flatMap((filter) => (filter.values ? this.transformFilter(filter) : []))
      .filter((filters) => filters.subgroups?.length || filters.criteria?.length);

    const newQuickFilters = quickFiltersTransformed.length ? quickFiltersTransformed : [];

    if (seriesId) {
      newQuickFilters.push({
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [
          {
            operator: EPointSearchOperator.EQUALS,
            key: EReminderDTOKeys.SERIES_ID,
            values: [seriesId],
          },
        ],
      });
    }

    if (this.keyword) {
      const newFilter: TSearchFilterSubgroup = {
        criteriaOperator: EAdvancedFilterLogic.OR,
        criteria: [],
      };

      const keysToSearch = [
        EReminderDTOKeys.DESCRIPTION,
        EReminderDTOKeys.NAME,
        EReminderDTOKeys.POINT_TITLE,
      ];

      for (const key of keysToSearch) {
        newFilter.criteria.push({
          operator: EPointSearchOperator.REGEX,
          key,
          values: [this.keyword],
        });
      }

      newQuickFilters.push(newFilter);
    }

    if (!newQuickFilters.length) {
      return undefined;
    }

    return {
      groupOperator: EAdvancedFilterLogic.AND,
      groups: newQuickFilters,
      criteria: [],
    };
  }

  private transformFilter(filter: TSearchFilterRow): TSearchFilterSubgroup[] {
    let newFilters: TSearchFilterSubgroup[] = [];

    switch (filter.keys[0]) {
      case EReminderFilterType.TYPE: {
        newFilters = this.transformTypeFilter(filter);

        break;
      }
      case EReminderFilterType.STATUS: {
        newFilters = this.transformStatusFilter(filter);

        break;
      }
      case EReminderFilterType.LINKED_TO: {
        newFilters = this.transformLinkedToFilter(filter);

        break;
      }
      case EReminderFilterType.DUE_DATE: {
        newFilters = this.transformDueDateFilter(filter);

        break;
      }
    }

    return [
      {
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [],
        subgroups: newFilters,
      },
    ];
  }

  private emitFiltersChange(): void {
    this._filtersChange$.next(this.filters);
  }

  private transformTypeFilter(filter: TSearchFilterRow): TSearchFilterSubgroup[] {
    const newFilters: TSearchFilterSubgroup[] = [];
    const userId = this.userService.getUser().userId;

    if (filter.values.length === 0) {
      return newFilters;
    }

    if (!filter.values.includes(EReminderFilterKeys.SHARED_BY_ME)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.OR,
        criteria: [
          {
            key: EReminderDTOKeys.IS_SHARED,
            operator: EPointSearchOperator.EQUALS,
            values: [false],
          },
          {
            key: EReminderDTOKeys.OWNER_ID,
            operator: EPointSearchOperator.NOT_EQUALS,
            values: [userId],
          },
        ],
      });
    }

    if (!filter.values.includes(EReminderFilterKeys.SHARED_WITH_ME)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [
          {
            key: EReminderDTOKeys.OWNER_ID,
            operator: EPointSearchOperator.EQUALS,
            values: [userId],
          },
        ],
      });
    }

    if (!filter.values.includes(EReminderFilterKeys.PERSONAL)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [
          {
            key: EReminderDTOKeys.IS_SHARED,
            operator: EPointSearchOperator.EQUALS,
            values: [true],
          },
        ],
      });
    }

    return newFilters;
  }

  private transformStatusFilter(filter: TSearchFilterRow): TSearchFilterSubgroup[] {
    const newFilters: TSearchFilterSubgroup[] = [];

    if (filter.values.length === 0) {
      return newFilters;
    }

    if (!filter.values.includes(EReminderFilterKeys.UPCOMING)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [
          {
            key: EReminderDTOKeys.TIMESTAMP_EPOCH_MILLIS,
            operator: EPointSearchOperator.LESS_THAN_OR_EQUAL,
            values: [new Date().getTime()],
          },
        ],
      });
    }

    if (!filter.values.includes(EReminderFilterKeys.OVERDUE)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.OR,
        criteria: [
          {
            key: EReminderDTOKeys.TIMESTAMP_EPOCH_MILLIS,
            operator: EPointSearchOperator.GREATER_THAN_OR_EQUAL,
            values: [new Date().getTime()],
          },
          {
            key: EReminderDTOKeys.DISMISSED,
            operator: EPointSearchOperator.EQUALS,
            values: [true],
          },
        ],
      });
    }

    if (!filter.values.includes(EReminderFilterKeys.DISMISSED)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [
          {
            key: EReminderDTOKeys.DISMISSED,
            operator: EPointSearchOperator.IN,
            values: ['', false, null],
          },
        ],
      });
    }

    return newFilters;
  }

  private transformLinkedToFilter(filter: TSearchFilterRow): TSearchFilterSubgroup[] {
    const newFilters: TSearchFilterSubgroup[] = [];

    if (filter.values.length === 0) {
      return newFilters;
    }

    if (!filter.values.includes(EReminderFilterKeys.LINKED)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [
          {
            key: EReminderDTOKeys.POINT_ID,
            operator: EPointSearchOperator.IN,
            values: ['', null],
          },
        ],
      });
    }

    if (!filter.values.includes(EReminderFilterKeys.WITHOUT_LINK)) {
      newFilters.push({
        criteriaOperator: EAdvancedFilterLogic.AND,
        criteria: [
          {
            key: EReminderDTOKeys.POINT_ID,
            operator: EPointSearchOperator.NOT_IN,
            values: ['', null],
          },
        ],
      });
    }

    return newFilters;
  }

  private transformDueDateFilter(filter: TSearchFilterRow): TSearchFilterSubgroup[] {
    let searchFilter: TSearchFilterSubgroup;

    switch (filter.operator) {
      case EPointSearchOperator.BETWEEN: {
        searchFilter = generateBetweenFilter({
          ...filter,
          keys: [EReminderDTOKeys.TIMESTAMP_EPOCH_MILLIS],
        });
        break;
      }
      default: {
        searchFilter = {
          criteriaOperator: EAdvancedFilterLogic.AND,
          criteria: [
            {
              key: EReminderDTOKeys.TIMESTAMP_EPOCH_MILLIS,
              operator: filter.operator,
              values: filter.values,
            },
          ],
        };

        break;
      }
    }

    return [searchFilter];
  }
}
